import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faBullseye } from "@fortawesome/free-solid-svg-icons/faBullseye";
import { faAlignLeft } from "@fortawesome/free-solid-svg-icons/faAlignLeft";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";

import { PSA } from "../../../../../model/admin/AdminPSA";
import { Flex } from "../../../../../components/Flex";
import { AtiraImage } from "../../../../../components/Image";
import { getMediaUrl } from "../../../../../utils/Media";
import { FontSize, Text } from "../../../../../components/Text";
import { linkify } from "../../../../../utils/String";
import { Tooltip } from "../../../../../utils/local-antd";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { Breakpoints } from "../../../../../theme/Breakpoints";
import { Shadow } from "../../../../../theme/Shadow";
import { useAppSelector } from "../../../../../redux/store";
import { adminSliceSelectors } from "../../../../../redux/admin/admin.selector";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons/faTrashAlt";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons/faPaperPlane";

import { Button } from "../../../../../components/Button";

const CardContainer = styled.div<{ loading: boolean }>`
  position: relative;
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.sub};
  border-radius: ${Rounded.xl};
  padding: ${Spacing.l};
  transition: all 0.3s ease;
  opacity: ${({ loading }) => (loading ? 0.6 : 1)};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.m};
  box-shadow: ${Shadow.SM};
`;

const HeaderRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.xs};
`;

const IDBadge = styled.span`
  font-family: monospace;
  font-size: calc(${FontSize.xs} - 0.1rem);
  background: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.darkerSub};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.sm};
`;

const PublicIndicator = styled(Flex)`
  background: ${({ theme }) => theme.lightSub};
  border: 1px solid ${({ theme }) => theme.sub};
  padding: 4px 12px;
  border-radius: ${Rounded.pill};
  color: ${({ theme }) => theme.main};
  font-weight: 600;
  font-size: calc(${FontSize.xs} - 0.1rem);
`;

const ImageContainer = styled.div`
  width: 50%;
  height: 15rem;
  border-radius: ${Rounded.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.sub};
  background: ${({ theme }) => theme.lightSub};

  .ant-image,
  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 30%;
  }
`;

const BullhornIconWrapper = styled(Flex)`
  html[dir="rtl"] & {
    transform: rotateY(180deg);
  }
`;

const DescriptionWrapper = styled(Flex)`
  background: ${({ theme }) => theme.lightSub};
  padding: ${Spacing.m};
  border-radius: ${Rounded.md};
  border-inline-start: 4px solid ${({ theme }) => theme.main};

  span {
    font-size: ${FontSize.s};
    color: ${({ theme }) => theme.lightBlack};
    line-height: 1.5;
  }
`;

const TargetChip = styled.span`
  background: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.main};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  font-size: ${FontSize.xs};
  font-weight: 600;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;

  &::before {
    content: "#";
    margin-inline-end: ${Spacing.xs};
    opacity: 0.5;
  }
`;

const CardFooter = styled(Flex)`
  margin-top: auto;
  padding-top: ${Spacing.m};
  border-top: 1px solid ${({ theme }) => theme.sub};
  gap: ${Spacing.s};
`;

const ActionButton = styled(Button)<{ variant?: "danger" | "accent" | "main" }>`
  margin: 0;
  width: 12rem;
  background: transparent;
  border: 1px solid ${({ theme, variant }) => theme[variant || "main"]};
  color: ${({ theme, variant }) => theme[variant || "main"]};

  &:hover:not(:disabled) {
    background: ${({ theme, variant }) => theme[variant || "main"]};
    color: ${({ theme }) => theme.white};
  }

  &:active {
    transform: scale(0.98);
  }
`;

type AdminPSACardProps = {
  psa: PSA;
  onEdit: VoidFunction;
  onDelete: VoidFunction;
  onResend: VoidFunction;
};

export const AdminPSACard: React.FC<AdminPSACardProps> = ({
  psa,
  onEdit,
  onDelete,
  onResend,
}) => {
  const { t } = useTranslation();

  const loading = useAppSelector(adminSliceSelectors.selectPSALoading);

  return (
    <CardContainer loading={loading}>
      <HeaderRow>
        <IDBadge>
          {t("common.id")}: {psa._id}
        </IDBadge>

        {psa.isPublic ? (
          <Tooltip title={t("admin.psa.public.info")}>
            <PublicIndicator gap="xs" alignItems="center">
              <AtiraIcon icon={faInfoCircle} size="xs" color="main" />
              {t("common.public")}
            </PublicIndicator>
          </Tooltip>
        ) : null}
      </HeaderRow>
      {psa?.images?.[0] && (
        <ImageContainer>
          <AtiraImage preview src={getMediaUrl(psa.images[0])} />
        </ImageContainer>
      )}
      <Flex gap="xs" alignItems="center">
        <BullhornIconWrapper>
          <AtiraIcon icon={faBullhorn} color="main" size="sm" />
        </BullhornIconWrapper>

        <Text fontWeight="bold" color="lightBlack">
          {psa.title}
        </Text>
      </Flex>
      <Flex flexDirection="column" gap="l">
        {psa?.description ? (
          <Flex flexDirection="column" gap="xs">
            <Flex gap="s" marginBottom="xs">
              <AtiraIcon icon={faAlignLeft} color="darkerSub" />

              <Text color="darkerSub" fontSize="xs" fontWeight="bold">
                {t("common.description").toUpperCase()}
              </Text>
            </Flex>

            <DescriptionWrapper>
              <span
                dangerouslySetInnerHTML={{
                  __html: linkify(psa.description || ""),
                }}
              />
            </DescriptionWrapper>
          </Flex>
        ) : null}

        {!psa?.isPublic ? (
          <Flex flexDirection="column" gap="xs">
            <Flex gap="s" marginBottom="xs">
              <AtiraIcon icon={faBullseye} color="darkerSub" />

              <Text color="darkerSub" fontSize="xs" fontWeight="bold">
                {t("admin.psa.drawer.create.target.title").toUpperCase()}
              </Text>
            </Flex>

            <Flex gap="xs" flexWrap="wrap">
              {psa.target.map((target) => (
                <TargetChip key={target}>
                  {t(`admin.psa.drawer.create.target.${target.toLowerCase()}`)}
                </TargetChip>
              ))}
            </Flex>
          </Flex>
        ) : null}
      </Flex>
      <CardFooter>
        <ActionButton variant="accent" icon={faEdit} onClick={onEdit}>
          {t("common.edit")}
        </ActionButton>

        <Tooltip title={t("common.coming_soon")}>
          <ActionButton
            variant="main"
            icon={faPaperPlane}
            onClick={onResend}
            disabled
          >
            {t("common.resend")}
          </ActionButton>
        </Tooltip>

        <ActionButton variant="danger" icon={faTrashAlt} onClick={onDelete}>
          {t("common.delete")}
        </ActionButton>
      </CardFooter>
    </CardContainer>
  );
};
